import React, { createContext, useContext, useEffect, useMemo } from 'react';
import useAxios from 'axios-hooks';
// eslint-disable-next-line
import axios, { CancelToken } from 'axios';
import { useStateWithSessionStorage } from '../hooks/useStateWithSessionStorage';
import { useLocale } from '@util/providers/Locale';

const LicenseContext = createContext({});

const cancelSource = CancelToken.source();

const defaultValues = {
  licensePlans: null,
  USLicensePlans: null,
  CALicensePlans: null,
};

function LicenseProvider({ children }) {
  const [licensePlans, setLicensePlans] = useStateWithSessionStorage(
    'licensePlans',
    defaultValues.licensePlans
  );
  const [USLicensePlans, setUSLicensePlans] = useStateWithSessionStorage(
    'USLicensePlans',
    defaultValues.USLicensePlans
  );
  const [CALicensePlans, setCALicensePlans] = useStateWithSessionStorage(
    'CALicensePlans',
    defaultValues.CALicensePlans
  );

  const { locale, setSelectedCurrency } = useLocale();

  const contextValue = useMemo(
    () => ({
      licensePlans,
      setLicensePlans,
      USLicensePlans,
      setUSLicensePlans,
      CALicensePlans,
      setCALicensePlans,
      resetLicensePlans: () => setLicensePlans(defaultValues.licensePlans),
      resetUSLicensePlans: () =>
        setUSLicensePlans(defaultValues.USLicensePlans),
      resetCALicensePlans: () =>
        setCALicensePlans(defaultValues.CALicensePlans),
    }),
    // setLicensePlans / setUSLicensePlans / setCALicensePlans are stable
    // setState refs from useStateWithSessionStorage; safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [licensePlans, USLicensePlans, CALicensePlans]
  );

  const fastspringKey = process.env.FASTSPRING_PRODUCT_KEY;
  const USShopifyKey = process.env.SHOPIFY_PRODUCT_KEY_US;
  const CAShopifyKey = process.env.SHOPIFY_PRODUCT_KEY_CA;
  const serviceUrl = process.env.SERVICE_URL;

  // ---- axios-hooks: MANUAL mode so nothing runs during SSR ----
  const [
    { data: licenseCatalogData, error: licenseCatalogError },
    getLicenseCatalog,
  ] = useAxios(
    {
      method: 'GET',
      cancelToken: cancelSource.token,
    },
    { manual: true }
  );

  const [{ data: USLicenseCatalogData }, getUSLicenseCatalog] = useAxios(
    {
      method: 'GET',
      cancelToken: cancelSource.token,
    },
    { manual: true }
  );

  const [{ data: CALicenseCatalogData }, getCALicenseCatalog] = useAxios(
    {
      method: 'GET',
      cancelToken: cancelSource.token,
    },
    { manual: true }
  );

  // ---- Trigger requests only in the browser ----
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!serviceUrl || !fastspringKey) return;

    getLicenseCatalog({
      url: `${serviceUrl}subscriptions/perpetual?productId=${fastspringKey}&currency=EURO`,
    }).catch((err) => {
      if (!axios.isCancel(err)) console.error('EU plans fetch failed', err);
    });
  }, [getLicenseCatalog, serviceUrl, fastspringKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!serviceUrl || !USShopifyKey) return;

    getUSLicenseCatalog({
      url: `${serviceUrl}subscriptions/perpetual?productId=${USShopifyKey}&currency=USD`,
    }).catch((err) => {
      if (!axios.isCancel(err)) console.error('US plans fetch failed', err);
    });
  }, [getUSLicenseCatalog, serviceUrl, USShopifyKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!serviceUrl || !CAShopifyKey) return;

    getCALicenseCatalog({
      url: `${serviceUrl}subscriptions/perpetual?productId=${CAShopifyKey}&currency=CAD`,
    }).catch((err) => {
      if (!axios.isCancel(err)) console.error('CA plans fetch failed', err);
    });
  }, [getCALicenseCatalog, serviceUrl, CAShopifyKey]);

  // ---- currency selection when locale and license catalog data changes ----
  useEffect(() => {
    if (!locale || !licenseCatalogData) return;
    setSelectedCurrency(
      locale.localeDetails?.isoCode === 'CA'
        ? 'CAD'
        : locale.localeDetails?.isoCode === 'US'
          ? 'USD'
          : licenseCatalogData.pricing?.[locale.localeDetails?.isoCode]
              ?.currency
    );
    // eslint-disable-next-line
  }, [locale, licenseCatalogData]);

  // ---- react to new data ----
  useEffect(() => {
    if (!licenseCatalogData) return;

    const newLicensePlans = { ...licenseCatalogData };
    if (USLicensePlans && locale?.localeDetails?.isoCode === 'US') {
      newLicensePlans.pricing['US'] = USLicensePlans.pricing['USD'];
    }
    if (CALicensePlans && locale?.localeDetails?.isoCode === 'CA') {
      newLicensePlans.pricing['CA'] = CALicensePlans.pricing['CAD'];
    }
    setLicensePlans(newLicensePlans);
    // eslint-disable-next-line
  }, [licenseCatalogData]);

  useEffect(() => {
    if (USLicenseCatalogData) setUSLicensePlans(USLicenseCatalogData);
    // eslint-disable-next-line
  }, [USLicenseCatalogData]);

  useEffect(() => {
    if (CALicenseCatalogData) setCALicensePlans(CALicenseCatalogData);
    // eslint-disable-next-line
  }, [CALicenseCatalogData]);

  // ---- don’t crash SSR on fetch errors ----
  useEffect(() => {
    if (licenseCatalogError && typeof window !== 'undefined') {
      // keep old behavior: surface error in browser only
      // throw new Error('Error retrieving plan catalog.');
      console.error('Error retrieving plan catalog.', licenseCatalogError);
    }
    // eslint-disable-next-line
  }, [licenseCatalogError]);

  return (
    <LicenseContext.Provider value={contextValue}>
      {children}
    </LicenseContext.Provider>
  );
}

function useLicense() {
  const context = useContext(LicenseContext);
  if (context === undefined) {
    throw new Error('useLicense must be used within a LicenseContext');
  }
  return context;
}

export { LicenseProvider, useLicense };
